/**
 * Risk Section - Business Value Calculator
 * Sprint 7: Day 2
 *
 * Percentage-based risk buffer calculation:
 * - User sets risk percentages for Personnel, Technology, and Other costs
 * - Automatically calculates buffer amount for each category
 * - Displays total risk buffer with visual percentage indicators
 *
 * Formula: Risk Buffer = Cost Category Total × (Risk Percentage / 100)
 */

import React from 'react';

export interface RiskBuffer {
  personnelRiskPct: number;      // 0-50%
  technologyRiskPct: number;     // 0-50%
  otherCostsRiskPct: number;     // 0-50%
  personnelBuffer: number;       // Auto-calculated
  technologyBuffer: number;      // Auto-calculated
  otherCostsBuffer: number;      // Auto-calculated
  totalBuffer: number;           // Sum of all buffers
}

interface RiskSectionProps {
  personnelTotal: number;
  technologyTotal: number;
  otherCostsTotal: number;
  riskBuffer: RiskBuffer;
  onRiskUpdate: (field: keyof RiskBuffer, value: number) => void;
}

export const RiskSection: React.FC<RiskSectionProps> = ({
  personnelTotal,
  technologyTotal,
  otherCostsTotal,
  riskBuffer,
  onRiskUpdate
}) => {
  // Handle risk percentage input with validation (0-50%)
  const handleRiskPctChange = (field: keyof RiskBuffer, value: string) => {
    const numericValue = parseFloat(value) || 0;

    // Clamp to 0-50% range
    const clampedValue = Math.min(50, Math.max(0, numericValue));

    onRiskUpdate(field, clampedValue);
  };

  // Calculate percentage bar width
  const getBarWidth = (pct: number) => `${Math.min(100, (pct / 50) * 100)}%`;

  return (
    <div className="risk-section">
      <div className="section-help-text">
        <strong>Risk Buffer:</strong> Add contingency reserves to account for project uncertainties.
        Industry standard: 10-20% for personnel, 5-15% for technology.
        Risk buffer is NOT added to cost base for benefit calculations.
      </div>

      <div className="risk-table-container">
        <table className="risk-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Cost Category</th>
              <th style={{ textAlign: 'right', width: '140px' }}>Base Cost</th>
              <th style={{ textAlign: 'center', width: '180px' }}>Risk %</th>
              <th style={{ textAlign: 'right', width: '140px' }}>Buffer Amount</th>
            </tr>
          </thead>
          <tbody>
            {/* Personnel Risk */}
            <tr>
              <td>
                <strong>Personnel Costs</strong>
              </td>
              <td style={{ textAlign: 'right', color: '#718096' }}>
                ${personnelTotal.toLocaleString()}
              </td>
              <td>
                <div className="risk-slider-container">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={riskBuffer.personnelRiskPct}
                    onChange={(e) => handleRiskPctChange('personnelRiskPct', e.target.value)}
                    className="risk-slider"
                    aria-label="Personnel risk percentage"
                  />
                  <div className="risk-pct-display">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={riskBuffer.personnelRiskPct}
                      onChange={(e) => handleRiskPctChange('personnelRiskPct', e.target.value)}
                      className="risk-pct-input"
                      aria-label="Personnel risk percentage value"
                    />
                    <span>%</span>
                  </div>
                </div>
                <div className="risk-bar">
                  <div
                    className="risk-bar-fill"
                    style={{ width: getBarWidth(riskBuffer.personnelRiskPct) }}
                  />
                </div>
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#0094ab' }}>
                ${riskBuffer.personnelBuffer.toLocaleString()}
              </td>
            </tr>

            {/* Technology Risk */}
            <tr>
              <td>
                <strong>Technology Costs</strong>
              </td>
              <td style={{ textAlign: 'right', color: '#718096' }}>
                ${technologyTotal.toLocaleString()}
              </td>
              <td>
                <div className="risk-slider-container">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={riskBuffer.technologyRiskPct}
                    onChange={(e) => handleRiskPctChange('technologyRiskPct', e.target.value)}
                    className="risk-slider"
                    aria-label="Technology risk percentage"
                  />
                  <div className="risk-pct-display">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={riskBuffer.technologyRiskPct}
                      onChange={(e) => handleRiskPctChange('technologyRiskPct', e.target.value)}
                      className="risk-pct-input"
                      aria-label="Technology risk percentage value"
                    />
                    <span>%</span>
                  </div>
                </div>
                <div className="risk-bar">
                  <div
                    className="risk-bar-fill"
                    style={{ width: getBarWidth(riskBuffer.technologyRiskPct) }}
                  />
                </div>
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#0094ab' }}>
                ${riskBuffer.technologyBuffer.toLocaleString()}
              </td>
            </tr>

            {/* Other Costs Risk */}
            <tr>
              <td>
                <strong>Other Costs</strong>
                <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.25rem' }}>
                  Training, Migration, Compliance, Vendor, Disruption
                </div>
              </td>
              <td style={{ textAlign: 'right', color: '#718096' }}>
                ${otherCostsTotal.toLocaleString()}
              </td>
              <td>
                <div className="risk-slider-container">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={riskBuffer.otherCostsRiskPct}
                    onChange={(e) => handleRiskPctChange('otherCostsRiskPct', e.target.value)}
                    className="risk-slider"
                    aria-label="Other costs risk percentage"
                  />
                  <div className="risk-pct-display">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={riskBuffer.otherCostsRiskPct}
                      onChange={(e) => handleRiskPctChange('otherCostsRiskPct', e.target.value)}
                      className="risk-pct-input"
                      aria-label="Other costs risk percentage value"
                    />
                    <span>%</span>
                  </div>
                </div>
                <div className="risk-bar">
                  <div
                    className="risk-bar-fill"
                    style={{ width: getBarWidth(riskBuffer.otherCostsRiskPct) }}
                  />
                </div>
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#0094ab' }}>
                ${riskBuffer.otherCostsBuffer.toLocaleString()}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #2d3748' }}>
              <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700, padding: '1rem' }}>
                Total Risk Buffer:
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.125rem', color: '#0094ab', padding: '1rem' }}>
                ${riskBuffer.totalBuffer.toLocaleString()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <style>{`
        .section-help-text {
          background: rgba(0, 148, 171, 0.05);
          border-left: 4px solid #0094ab;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.6;
        }

        .risk-table-container {
          overflow-x: auto;
        }

        .risk-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
        }

        .risk-table thead th {
          padding: 0.75rem 1rem;
          font-size: 0.75rem;
          font-weight: 700;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: #f7fafc;
          border-bottom: 2px solid #e2e8f0;
        }

        .risk-table tbody td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: top;
        }

        .risk-table tbody tr:hover {
          background: #f7fafc;
        }

        .risk-slider-container {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .risk-slider {
          flex: 1;
          height: 6px;
          border-radius: 3px;
          background: linear-gradient(to right, #e2e8f0 0%, #0094ab 100%);
          outline: none;
          -webkit-appearance: none;
          appearance: none;
          cursor: pointer;
        }

        .risk-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #0094ab;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .risk-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #0094ab;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .risk-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.3);
        }

        .risk-slider:focus::-moz-range-thumb {
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.3);
        }

        .risk-pct-display {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #2d3748;
        }

        .risk-pct-input {
          width: 50px;
          padding: 0.375rem 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          text-align: right;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .risk-pct-input:focus {
          outline: none;
          border-color: #0094ab;
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.1);
        }

        .risk-bar {
          width: 100%;
          height: 4px;
          background: #e2e8f0;
          border-radius: 2px;
          margin-top: 0.5rem;
          overflow: hidden;
        }

        .risk-bar-fill {
          height: 100%;
          background: linear-gradient(to right, #0094ab, #004772);
          border-radius: 2px;
          transition: width 0.3s ease;
        }
      `}</style>
    </div>
  );
};

// Default risk buffer with industry-standard percentages
export const DEFAULT_RISK_BUFFER: RiskBuffer = {
  personnelRiskPct: 15,          // 15% risk on personnel
  technologyRiskPct: 10,         // 10% risk on technology
  otherCostsRiskPct: 12,         // 12% risk on other costs
  personnelBuffer: 0,            // Auto-calculated
  technologyBuffer: 0,           // Auto-calculated
  otherCostsBuffer: 0,           // Auto-calculated
  totalBuffer: 0                 // Auto-calculated
};

export default RiskSection;
